/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-02-12 15:40:50                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-02-25 16:02:48                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Services
  * DetailsDrawer: display details drawer
*/

/* Imports */
import { Button, Card, CardBody, CardFooter, Typography } from "@material-tailwind/react";
import { Modal } from "@mui/material";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { showApiErrorToast } from "../../../services/api/error";
import { CalendarIcon, XMarkIcon } from "@heroicons/react/24/solid";
/***/

/* Components */
import CustomerDisplay from "../../../components/data/customer/CustomerDisplay";
import AddressDisplay from "../../../components/data/address/AddressDisplay";
import DateDisplay from "../../../components/data/date/DateDisplay";
import TechnicianSelect from "../../../components/data/technician/TechnicianSelect";
import TechnicianDisplay from "../../../components/data/technician/TechnicianDisplay";
import WorkordersStatusDisplay from "../../../components/data/workorder-status/WorkordersStatusDisplay";
import DateRangeSelect from "../../../components/data/date-range/DateRangeSelect";
import { WorkorderStatus } from "../../../types/workorder/workorderStatus";
/***/

/* Services */
import { api } from "../../../services/api/api";
/***/

import "./PlanningModal.scss";
import Loading from "../../../components/loading/Loading";

/**
* DetailsDrawer: display details drawer
* @param time - time to display
* @param onUpdate - on update callback, return updated time
*/
export default function WorkorderEditModal({
  open,
  workorder,
  defaultEdit,
  onUpdate,
  onClose
}: {
  open: boolean,
  defaultEdit: string,
  workorder: EasyTech.Workorder,
  onUpdate: (workorder: EasyTech.Workorder) => void
  onClose: () => void
}) {
  const { t } = useTranslation();
  const [onEdit, setOnEdit] = useState<string | null>(defaultEdit);
  const [tech, setTech] = useState<EasyTech.Technician | null>(null);
  const [scheduleState, setSchedule] = useState<{start: string, end: string | null} | null>(null);
  const [loadingState, setLoadingState] = useState<boolean>(false);

  /**
  * save: Save
  */
  const save = (): void => {
    saveEditWorkorder();
  };
  /***/

  /**
  * saveEditWorkorder: Save edition change
  */
  const saveEditWorkorder = (): void => {
    setLoadingState(true);
    if (onEdit === "schedule") 
      saveSchedule();
    else
      saveTech();
  };
  /***/

  const saveSchedule = ():void =>{
    let schedule: any = {
      start: scheduleState ? scheduleState.start : workorder.schedule?.start,
      end: scheduleState ? scheduleState.end : workorder.schedule?.end
    };
    if (!schedule.start || !schedule.end)
      schedule = { start: null, end: null, duration: null };

    const updatePayload: any = {
      schedule: schedule
    };

    api.put(`admin/workorders/${workorder.id}`, updatePayload).then((ret) => {
      onUpdate(ret as EasyTech.Workorder);
      setOnEdit(null);
      toast.success(t("workorder:notifications:update:success"));
    }).catch((err) => {
      showApiErrorToast(err);
    }).finally(() => {
      setLoadingState(false);
      onClose();
    });
  };

  const saveTech = (): void => {
    api.put(`admin/workorders/${workorder.id}/assign`, tech ? tech : workorder.technician).then((ret) => {
      const update = ret as EasyTech.Workorder;
      onUpdate({...update, technician:  tech ? tech : workorder.technician});
      setOnEdit(null);
      toast.success(t("workorder:notifications:update:success"));
    }).catch((err) => {
      showApiErrorToast(err);
    }).finally(() => {
      setLoadingState(false);
      onClose();
    });
  };

  return(
    
    <Modal
      open={open}
      onClose={onClose}
      className="te-modal"
      aria-labelledby="te-modal"
      aria-describedby="te-modal">
      <Card className="te-modal-content  rounded-lg">
        <button className="close-button" disabled={loadingState} onClick={() => onClose()}>
          <XMarkIcon className="w-4"/>
        </button>
        <CardBody>
          <div  className="flex items-center gap-3 mb-5">
            <div className="rounded-full border p-2 bg-light-green-200">
              <CalendarIcon className="w-7 text-green-700" /> 
            </div>
            <Typography variant="h5">
              {t("planification:scheduleIntervention")} <a href={"/workorders/"+workorder.id}>{workorder.ref}</a>
            </Typography>
          </div>
         
          <div className="flex justify-between">
            <CustomerDisplay customer={workorder.customer as EasyTech.Customer}/>
            <WorkordersStatusDisplay status={workorder.status}/>
          </div>
          
          
          <div className="flex justify-between">
            {workorder.technician && <TechnicianDisplay technician={workorder.technician}/>}
            {workorder.schedule && <DateDisplay date={new Date(workorder.schedule.start)}/> }
          </div>


          <AddressDisplay address={workorder.address} zone={workorder.zone}/>
          

          {onEdit === "tech" &&
            <div className="py-4">
              <TechnicianSelect onChange={(el) => setTech(el)}
                  value={workorder.technician}
                  address={workorder.address}
                  schedule={workorder.schedule}
                  isDetailed={true}/>
            </div>
          }

          {onEdit === "schedule" &&
            <div className="py-2">
              <DateRangeSelect  range={scheduleState || {start: workorder.schedule ? workorder.schedule.start : "", end: workorder.schedule ? workorder.schedule.end : ""}}
                                onChange={el => setSchedule(el)}/>
            </div>
          }

          
        </CardBody>
        <CardFooter className="pt-0 flex gap-2 justify-end">
          {!onEdit ?
            <>
              <Button color="blue"
                      disabled={workorder.status === WorkorderStatus.IN_PROGRESS}
                      onClick={() => setOnEdit("schedule")}>
                {t("planification:reSchedule")}
              </Button>

              <Button color="blue"
                      disabled={workorder.status === WorkorderStatus.IN_PROGRESS}
                      onClick={() => setOnEdit("tech")}>
                {t("planification:reAssign")}
              </Button>
            </>
          : 
            <>
              <Button variant="outlined"
                      disabled={loadingState}
                      onClick={() => setOnEdit(null)}>
                {t("planification:cancel")}
              </Button>

              <Button color="blue"
                      disabled={loadingState}
                      onClick={() => save()}>
                {loadingState ?
                  <Loading size="h-4 w-4"/>
                :
                  t("planification:save")
                }
              </Button>
            </>
          }
        </CardFooter>
      </Card>
    </Modal>
  );
}
/***/
